import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lightbulb, Scan, Palette, GitBranch, ChevronRight, Loader2, Save, RefreshCw, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { getCurrentUserId } from '../../utils/currentUser';

const API = process.env.REACT_APP_BACKEND_URL;

const TOOLS = [
  { id: 'design_hints', label: 'Design Hints', icon: Lightbulb, desc: 'AI-generated observations on your project', color: '#F59E0B' },
  { id: 'room_analysis', label: 'Room Analysis', icon: Scan, desc: 'Room-by-room proportion & feature check', color: '#3B82F6' },
  { id: 'style_check', label: 'Style Check', icon: Palette, desc: 'Flag material/finish inconsistencies', color: '#8B5CF6' },
  { id: 'opportunity_scan', label: 'Opportunity Scanner', icon: Star, desc: 'Find underutilized spaces', color: '#10B981' },
  { id: 'adjacency', label: 'Adjacency & Flow', icon: GitBranch, desc: 'Optimal room relationships', color: '#EC4899' },
];

export default function DesignIntelligence({ projectId, rooms = [], darkMode = false }) {
  const [activeTool, setActiveTool] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [targetStyle, setTargetStyle] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('');
  const [customContext, setCustomContext] = useState('');
  const [savedOutputs, setSavedOutputs] = useState([]);

  useEffect(() => {
    if (projectId) loadOutputs();
  }, [projectId]);

  const loadOutputs = async () => {
    try {
      const res = await axios.get(`${API}/api/design-companion/outputs/${projectId || 'standalone'}?user_id=${getCurrentUserId()}`);
      setSavedOutputs((res.data.outputs || []).filter(o => o.source === 'design_intelligence').slice(0, 20));
    } catch {}
  };

  const runAnalysis = async (section) => {
    setLoading(true);
    setResult(null);
    try {
      const params = new URLSearchParams({
        section,
        user_id: getCurrentUserId(),
        ...(projectId && { project_id: projectId }),
        ...(targetStyle && { target_style: targetStyle }),
        ...(selectedRoom && { room_name: selectedRoom }),
        ...(customContext && { custom_context: customContext }),
      });
      const res = await axios.post(`${API}/api/design-intelligence/generate?${params}`);
      setResult({ section, response: res.data.response, id: res.data.id });
      toast.success('Analysis complete');
      if (projectId) loadOutputs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const bg = darkMode ? 'bg-[#1A1A1A]' : 'bg-white';
  const cardBg = darkMode ? 'bg-[#222226]' : 'bg-[#FAFAF8]';
  const border = darkMode ? 'border-[#333]' : 'border-gray-200';
  const text = darkMode ? 'text-gray-100' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const inputBg = darkMode ? 'bg-[#2A2A2E] border-[#444] text-gray-100' : '';

  return (
    <div className={`flex flex-col h-full ${bg}`}>
      {/* Tool selector cards */}
      {!activeTool && (
        <div className="p-6">
          <h2 className={`text-lg font-semibold mb-1 ${text}`}>Design Intelligence</h2>
          <p className={`text-sm mb-6 ${textMuted}`}>
            {projectId ? 'AI-powered analysis using your project data' : 'Select a project for contextual analysis, or run in reference mode'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TOOLS.map(tool => (
              <button
                key={tool.id}
                onClick={() => setActiveTool(tool.id)}
                className={`${cardBg} border ${border} rounded-xl p-5 text-left hover:border-[#BC5D41]/50 transition-all group`}
                data-testid={`di-tool-${tool.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${tool.color}15` }}>
                    <tool.icon className="w-4.5 h-4.5" style={{ color: tool.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium text-sm ${text} group-hover:text-[#BC5D41] transition-colors`}>{tool.label}</div>
                    <div className={`text-xs mt-0.5 ${textMuted}`}>{tool.desc}</div>
                  </div>
                  <ChevronRight className={`w-4 h-4 mt-0.5 ${textMuted} opacity-0 group-hover:opacity-100 transition-opacity`} />
                </div>
              </button>
            ))}
          </div>

          {/* Recent outputs */}
          {savedOutputs.length > 0 && (
            <div className="mt-8">
              <h3 className={`text-sm font-medium mb-3 ${textMuted}`}>Recent Analyses</h3>
              <div className="space-y-2">
                {savedOutputs.slice(0, 5).map(o => (
                  <button key={o.id}
                    onClick={() => { setActiveTool(o.category?.replace('intelligence_', '')); setResult({ section: o.category, response: o.raw_response, id: o.id }); }}
                    className={`w-full text-left px-4 py-2.5 rounded-lg ${cardBg} border ${border} hover:border-[#BC5D41]/40 transition-colors`}>
                    <div className={`text-xs font-medium ${text}`}>{o.title}</div>
                    <div className={`text-xs ${textMuted} mt-0.5`}>{new Date(o.created_at).toLocaleDateString()} — {o.raw_response?.substring(0, 80)}...</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Active tool view */}
      {activeTool && (
        <div className="flex flex-col h-full">
          {/* Tool header */}
          <div className={`px-6 py-4 border-b ${border} flex items-center gap-3`}>
            <button onClick={() => { setActiveTool(null); setResult(null); }}
              className={`text-xs ${textMuted} hover:text-[#BC5D41]`}>&larr; Back</button>
            <div className={`font-medium text-sm ${text}`}>
              {TOOLS.find(t => t.id === activeTool)?.label}
            </div>
          </div>

          <div className="flex-1 flex overflow-hidden">
            {/* Input panel */}
            <div className={`w-80 flex-shrink-0 border-r ${border} p-5 space-y-4 overflow-y-auto`}>
              {activeTool === 'style_check' && (
                <div>
                  <label className={`text-xs font-medium ${textMuted}`}>Target Style</label>
                  <Select value={targetStyle} onValueChange={setTargetStyle}>
                    <SelectTrigger className={`mt-1 ${inputBg}`} data-testid="style-select">
                      <SelectValue placeholder="Choose style..." />
                    </SelectTrigger>
                    <SelectContent>
                      {['Modern Farmhouse', 'Transitional', 'Craftsman', 'Contemporary', 'Traditional', 'Mid-Century Modern', 'Coastal', 'Industrial', 'Mediterranean', 'Scandinavian'].map(s =>
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {activeTool === 'room_analysis' && (
                <div>
                  <label className={`text-xs font-medium ${textMuted}`}>Select Room</label>
                  {rooms.length > 0 ? (
                    <Select value={selectedRoom} onValueChange={setSelectedRoom}>
                      <SelectTrigger className={`mt-1 ${inputBg}`} data-testid="room-select">
                        <SelectValue placeholder="Choose room..." />
                      </SelectTrigger>
                      <SelectContent>
                        {rooms.map(r => <SelectItem key={r.id || r.name} value={r.name}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input className={`mt-1 ${inputBg}`} placeholder="Enter room name" value={selectedRoom} onChange={e => setSelectedRoom(e.target.value)} data-testid="room-input" />
                  )}
                </div>
              )}

              <div>
                <label className={`text-xs font-medium ${textMuted}`}>Additional Context</label>
                <Textarea className={`mt-1 text-sm ${inputBg}`} rows={4} placeholder="Add any notes, constraints, or specific areas to focus on..."
                  value={customContext} onChange={e => setCustomContext(e.target.value)} data-testid="context-input" />
              </div>

              {!projectId && (
                <div className={`text-xs p-3 rounded-lg ${darkMode ? 'bg-amber-900/20 text-amber-300' : 'bg-amber-50 text-amber-700'}`}>
                  Running in reference mode — connect a project for contextual analysis.
                </div>
              )}

              <Button onClick={() => runAnalysis(activeTool)} disabled={loading}
                className="w-full bg-[#BC5D41] hover:bg-[#70170F] text-white" data-testid="run-analysis-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</> : 'Run Analysis'}
              </Button>
            </div>

            {/* Result panel */}
            <ScrollArea className="flex-1 p-6">
              {loading && (
                <div className="flex flex-col items-center justify-center h-64 gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-[#BC5D41]" />
                  <span className={`text-sm ${textMuted}`}>Analyzing project data...</span>
                </div>
              )}
              {!loading && !result && (
                <div className={`text-center py-20 ${textMuted}`}>
                  <div className="text-sm">Configure inputs and click "Run Analysis"</div>
                </div>
              )}
              {!loading && result && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`font-semibold text-sm ${text}`}>{TOOLS.find(t => t.id === activeTool)?.label} Results</h3>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => runAnalysis(activeTool)}
                        className={darkMode ? 'border-[#444] text-gray-300' : ''}>
                        <RefreshCw className="w-3 h-3 mr-1" /> Regenerate
                      </Button>
                    </div>
                  </div>
                  <div className={`prose prose-sm max-w-none ${darkMode ? 'prose-invert' : ''}`}
                    data-testid="analysis-result">
                    {result.response.split('\n').map((line, i) => {
                      if (line.startsWith('# ')) return <h2 key={i} className="text-base font-bold mt-4 mb-2">{line.slice(2)}</h2>;
                      if (line.startsWith('## ')) return <h3 key={i} className="text-sm font-semibold mt-3 mb-1">{line.slice(3)}</h3>;
                      if (line.startsWith('### ')) return <h4 key={i} className="text-sm font-medium mt-2 mb-1">{line.slice(4)}</h4>;
                      if (line.startsWith('- ') || line.startsWith('* ')) return <li key={i} className="ml-4 text-sm">{line.slice(2)}</li>;
                      if (line.match(/^\d+\./)) return <li key={i} className="ml-4 text-sm list-decimal">{line.replace(/^\d+\.\s*/, '')}</li>;
                      if (line.startsWith('|')) return <pre key={i} className={`text-xs p-1 ${darkMode ? 'bg-[#2A2A2E]' : 'bg-gray-50'} rounded`}>{line}</pre>;
                      if (line.trim() === '') return <br key={i} />;
                      return <p key={i} className="text-sm leading-relaxed">{line}</p>;
                    })}
                  </div>
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      )}
    </div>
  );
}
